"use client";

/**
 * Client islands for the manufacturer PCN access guide:
 *  - ManufacturerGuideView: fires manufacturer_guide_view once on mount.
 *  - ManufacturerSection: a per-manufacturer accordion (<details>, content in
 *    HTML for SEO) that fires manufacturer_section_expand on first open,
 *    manufacturer_resource_click on the official outbound link, and
 *    manufacturer_pcnshark_cta_click on the in-section PCNshark CTA.
 *
 * Analytics never carry sensitive data (manufacturer name + resource type only)
 * and never block rendering/navigation.
 */
import * as React from "react";
import posthog from "posthog-js";
import type { ManufacturerAccessEntry } from "@/lib/seo/content/types";

function capture(event: string, props?: Record<string, string>) {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics must never break the page */
  }
}

function fmtDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function ManufacturerGuideView() {
  React.useEffect(() => {
    capture("manufacturer_guide_view", { source_page: "/resources/manufacturer-pcn-directory" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function FactValue({ value }: { value: string }) {
  const muted = value === "Not clearly documented";
  return <span className={muted ? "ink-3 italic" : "ink-2"}>{value}</span>;
}

const ACCESS_ROWS: Array<[string, keyof ManufacturerAccessEntry["facts"]]> = [
  ["Public archive", "publicArchive"],
  ["Searchable historical notices", "searchableHistorical"],
  ["Account required", "accountRequired"],
  ["Email notification", "emailNotification"],
  ["Part-number subscription", "partNumberSubscription"],
  ["Customer-specific notification", "customerSpecific"],
  ["Distributor involvement", "distributorInvolvement"],
];

export function ManufacturerSection({
  entry,
  pcnsharkIntake,
  pcnsharkHref,
}: {
  entry: ManufacturerAccessEntry;
  pcnsharkIntake: string[];
  /** Signup/onboarding href for the in-section "Process a … PCN" CTA. */
  pcnsharkHref: string;
}) {
  const opened = React.useRef(false);

  return (
    <details
      id={entry.id}
      className="group scroll-mt-20 border rule bg-white"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && !opened.current) {
          opened.current = true;
          capture("manufacturer_section_expand", { manufacturer: entry.id });
        }
      }}
    >
      <summary className="flex items-start justify-between gap-4 px-5 py-4 cursor-pointer list-none">
        <span>
          <span className="font-display font-semibold text-[16px] leading-snug">
            {entry.name}
          </span>
          <span className="block mt-0.5 font-mono text-[10px] uppercase tracking-wider ink-3">
            {entry.sourceType} · verified {fmtDate(entry.lastVerified)}
          </span>
        </span>
        <span className="font-mono text-[14px] ink-3 group-open:rotate-45 transition-transform shrink-0 pt-1">
          ＋
        </span>
      </summary>

      <div className="px-5 pb-6 pt-1 border-t rule">
        <p className="text-[14px] ink-2 leading-relaxed max-w-[72ch]">
          {entry.summary}
        </p>

        <h3 className="mt-5 font-mono text-[11px] uppercase tracking-wider ink-3">
          How notifications work
        </h3>
        <ul className="mt-2 space-y-1.5 max-w-[72ch]">
          {entry.howNotificationsWork.map((b, i) => (
            <li
              key={i}
              className="grid grid-cols-[14px_1fr] gap-2 text-[13.5px] ink-2 leading-relaxed"
            >
              <span aria-hidden className="font-mono ink-3" style={{ marginTop: "1px" }}>
                +
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <h3 className="mt-5 font-mono text-[11px] uppercase tracking-wider ink-3">
          What your team should do
        </h3>
        <ol className="mt-2 space-y-1.5 max-w-[72ch]">
          {entry.whatYourTeamShouldDo.map((s, i) => (
            <li
              key={i}
              className="grid grid-cols-[20px_1fr] gap-2 text-[13.5px] ink-2 leading-relaxed"
            >
              <span className="font-mono text-[11px] ink-3" style={{ marginTop: "1px" }}>
                {i + 1}.
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          {/* Access summary */}
          <div>
            <h3 className="font-mono text-[11px] uppercase tracking-wider ink-3 mb-2">
              Access summary
            </h3>
            <dl className="border-t rule">
              {ACCESS_ROWS.map(([label, key]) => (
                <div
                  key={key}
                  className="grid grid-cols-[1fr_1.2fr] gap-3 py-2 border-b rule text-[12.5px]"
                >
                  <dt className="ink-3">{label}</dt>
                  <dd className="leading-snug">
                    <FactValue value={entry.facts[key]} />
                  </dd>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_1.2fr] gap-3 py-2 text-[12.5px]">
                <dt className="ink-3">Date last verified</dt>
                <dd className="ink-2 font-mono">{fmtDate(entry.lastVerified)}</dd>
              </div>
            </dl>
          </div>

          {/* Through PCNshark */}
          <div>
            <h3 className="font-mono text-[11px] uppercase tracking-wider ink-3 mb-2">
              Through PCNshark
            </h3>
            <ul className="space-y-1.5">
              {pcnsharkIntake.map((p, i) => (
                <li
                  key={i}
                  className="grid grid-cols-[14px_1fr] gap-2 text-[13px] ink-2 leading-relaxed"
                >
                  <span
                    aria-hidden
                    className="font-mono"
                    style={{ color: "var(--shark-accent)", marginTop: "1px" }}
                  >
                    +
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {entry.uncertain && entry.uncertain.length > 0 && (
          <p className="mt-5 font-mono text-[11px] ink-3 leading-relaxed max-w-[80ch]">
            Not clearly documented in {entry.name}&rsquo;s public materials:{" "}
            {entry.uncertain.join("; ")}.
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href={entry.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              capture("manufacturer_resource_click", {
                manufacturer: entry.id,
                resource_type: entry.sourceType,
                destination: "manufacturer_site",
              })
            }
            className="inline-flex items-center justify-center px-4 py-2.5 border rule text-[13px] font-medium ink-2 hover:border-[color:var(--ink)] hover:text-[color:var(--ink)] transition"
          >
            Open official {entry.name} PCN resource ↗
          </a>
          <a
            href={pcnsharkHref}
            onClick={() =>
              capture("manufacturer_pcnshark_cta_click", {
                manufacturer: entry.id,
                cta_location: "manufacturer_section",
                destination: "signup",
              })
            }
            className="inline-flex items-center justify-center px-4 py-2.5 bg-[color:var(--ink)] text-white text-[13px] font-medium hover:bg-black transition"
          >
            Process a {entry.name} PCN in PCNshark
          </a>
        </div>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-wider ink-3">
          Official link opens {entry.name}&rsquo;s site in a new tab
        </p>
      </div>
    </details>
  );
}
